/**
 * Subscription Service
 * Handles subscription management and Stripe integration operations
 */

import { apiClient } from './api';
import { serviceProviderService } from './service-provider.service';
import type {
  Subscription,
  SubscriptionResponse,
} from './service-provider.service';
import {
  ensureSubscription,
  ensureCheckoutSessionResponse,
  ensureCancelSubscriptionResponse,
  ensureStripeOnboardingResponse,
  ensureStripeAccountStatus,
  ensureStripeInvoicesResponse,
  ensureSetupIntentResponse,
  ensurePaymentMethodResponse,
} from '@/lib/api/type-guards';

/**
 * Checkout session response from Stripe
 */
export interface CheckoutSessionResponse {
  sessionId: string;
  url: string;
}

/**
 * Stripe Connect onboarding response
 */
export interface StripeOnboardingResponse {
  accountId: string;
  onboardingUrl: string;
}

/**
 * Stripe Connect account status
 */
export interface StripeAccountStatus {
  charges_enabled: boolean;
  details_submitted: boolean;
  payouts_enabled: boolean;
  requirements?: {
    currently_due: string[];
    eventually_due: string[];
    past_due: string[];
  };
}

/**
 * Subscription cancellation response
 */
export interface CancelSubscriptionResponse {
  success: boolean;
  cancels_at: number; // Unix timestamp
}

/**
 * Invoice data structure
 */
export interface Invoice {
  id: string;
  created: number; // Unix timestamp
  amount_paid: number; // In cents
  status: 'paid' | 'open' | 'void' | 'uncollectible';
  hosted_invoice_url: string;
  invoice_pdf: string;
}

/**
 * Invoices list response
 */
export interface InvoicesResponse {
  invoices: Invoice[];
  total: number;
}

export const subscriptionService = {
  /**
   * Get current service provider's subscription
   */
  async getSubscription(): Promise<Subscription> {
    const token = serviceProviderService.getToken();
    const data = await apiClient.get<SubscriptionResponse>(
      '/service-provider/subscription',
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return ensureSubscription(data.subscription);
  },

  /**
   * Create a Stripe Checkout session for subscription upgrade
   */
  async createCheckoutSession(tier: 'basic' | 'pro' | 'enterprise'): Promise<CheckoutSessionResponse> {
    const token = serviceProviderService.getToken();
    const data = await apiClient.post<CheckoutSessionResponse>(
      '/service-provider/stripe/checkout',
      { tier },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return ensureCheckoutSessionResponse(data);
  },

  /**
   * Cancel current subscription (will cancel at end of billing period)
   */
  async cancelSubscription(): Promise<CancelSubscriptionResponse> {
    const token = serviceProviderService.getToken();
    const data = await apiClient.post<CancelSubscriptionResponse>(
      '/service-provider/subscription/cancel',
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return ensureCancelSubscriptionResponse(data);
  },

  /**
   * Get Stripe Connect onboarding link to enable payment processing
   */
  async getStripeOnboardingLink(): Promise<StripeOnboardingResponse> {
    const token = serviceProviderService.getToken();
    const data = await apiClient.post<StripeOnboardingResponse>(
      '/service-provider/stripe/onboard',
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return ensureStripeOnboardingResponse(data);
  },

  /**
   * Get Stripe Connect account status to check onboarding completion
   */
  async getStripeStatus(): Promise<StripeAccountStatus> {
    const token = serviceProviderService.getToken();
    const data = await apiClient.get<StripeAccountStatus>(
      '/service-provider/stripe/status',
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return ensureStripeAccountStatus(data);
  },

  /**
   * Get billing invoices (if backend supports this endpoint)
   */
  async getInvoices(): Promise<InvoicesResponse> {
    const token = serviceProviderService.getToken();
    const data = await apiClient.get<InvoicesResponse>(
      '/service-provider/subscription/invoices',
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return ensureStripeInvoicesResponse(data);
  },

  /**
   * Create a SetupIntent for updating payment method
   */
  async getSetupIntent(): Promise<{ client_secret: string; setup_intent_id: string }> {
    const token = serviceProviderService.getToken();
    const data = await apiClient.post<{ client_secret: string; setup_intent_id: string }>(
      '/service-provider/stripe/setup-intent',
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return ensureSetupIntentResponse(data);
  },

  /**
   * Get current payment method on file
   */
  async getPaymentMethod(): Promise<{
    id: string;
    card: {
      brand: string;
      last4: string;
      exp_month: number;
      exp_year: number;
    };
  }> {
    const token = serviceProviderService.getToken();
    const data = await apiClient.get<{
      id: string;
      card: {
        brand: string;
        last4: string;
        exp_month: number;
        exp_year: number;
      };
    }>(
      '/service-provider/stripe/payment-method',
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return ensurePaymentMethodResponse(data);
  },
};
